import {
  Box,
  Button,
  Heading,
  Input,
  SimpleGrid,
  Stack,
  Text,
  Dialog,
  Badge
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { apiGet, apiDelete, apiUpload } from '../api.js';
import { useAuth } from '../context/AuthContext';
import { calculatePendingInterest } from '../utils/interestCalculation';

const formatDate = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const toDateInputValue = (value) => {
  if (!value) return '';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  if (
    d.getUTCHours() === 0 &&
    d.getUTCMinutes() === 0 &&
    d.getUTCSeconds() === 0
  ) {
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
  }
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const fmtMoney = (value) => {
  if (value == null || value === '') return 'N/A';
  return `₹${Number(value).toFixed(2)}`;
};

const releaseInterestOf = (article) => {
  if (!article) return null;
  const n = article.releaseInterest ?? article.interestAmount;
  return n == null ? null : Number(n);
};

const formatIdProof = (proof) => {
  if (!proof) return 'N/A';
  const first = Array.isArray(proof) ? proof[0] : proof;
  if (!first?.type) return 'N/A';
  return `${first.type}${first.number ? ` - ${first.number}` : ''}`;
};

const getArticleInterestRate = (article) => {
  if (article?.interestRate != null && article.interestRate !== '') {
    return String(article.interestRate);
  }
  const loans = article?.loans || [];
  for (const loan of loans) {
    if (loan?.interestRate != null && loan.interestRate !== '') {
      return String(loan.interestRate);
    }
  }
  return '';
};

const Released = () => {
  const { user } = useAuth();
  const canDelete = user?.role === 'admin' || user?.permissions?.articles?.delete;
  const canEdit = user?.role === 'admin' || user?.permissions?.articles?.edit;

  const [filters, setFilters] = useState({
    search: '',
    amountSearch: '',
    startDate: '',
    endDate: ''
  });
  const [articles, setArticles] = useState([]);
  const [error, setError] = useState('');
  const [viewArticle, setViewArticle] = useState(null);
  const [editingArticle, setEditingArticle] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  const fetchReleased = async (params = filters) => {
    const query = new URLSearchParams({ status: 'released' });
    Object.entries(params).forEach(([key, value]) => {
      if (value) query.append(key, value);
    });
    try {
      const result = await apiGet(`/transactions?${query.toString()}`);
      setArticles(result.articles || []);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load released items');
    }
  };

  useEffect(() => {
    fetchReleased();
  }, []);

  const handleFilterChange = (e) => {
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const applyFilters = (e) => {
    e.preventDefault();
    fetchReleased();
  };

  const clearFilters = () => {
    const cleared = { search: '', amountSearch: '', startDate: '', endDate: '' };
    setFilters(cleared);
    fetchReleased(cleared);
  };

  const openEdit = (article) => {
    setEditingArticle(article);
    setEditForm({
      nameOrNo: article.nameOrNo || '',
      weight: article.weight ?? '',
      interestRate: getArticleInterestRate(article),
      address: article.address || '',
      description: article.description || '',
      releaseDate: toDateInputValue(article.releaseDate),
      releaseAmount: article.releaseAmount ?? ''
    });
  };

  const previewInterest = useMemo(() => {
    if (!editingArticle || !editForm.releaseDate) return releaseInterestOf(editingArticle);
    return calculatePendingInterest(editingArticle, editForm.releaseDate);
  }, [editingArticle, editForm.releaseDate]);

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async () => {
    if (!editingArticle) return;
    setSavingEdit(true);
    try {
      const formData = new FormData();
      Object.entries(editForm).forEach(([key, value]) => {
        if (value !== null && value !== undefined) formData.append(key, value);
      });
      await apiUpload(`/articles/${editingArticle._id}`, formData, 'PUT');
      alert('Released item updated');
      setEditingArticle(null);
      if (viewArticle?._id === editingArticle._id) setViewArticle(null);
      fetchReleased();
    } catch (err) {
      alert(err.message || 'Failed to update');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (article) => {
    if (!window.confirm(`Delete released item "${article.nameOrNo || 'this item'}"? This cannot be undone.`)) return;
    try {
      await apiDelete(`/articles/${article._id}`);
      if (viewArticle?._id === article._id) setViewArticle(null);
      if (editingArticle?._id === article._id) setEditingArticle(null);
      fetchReleased();
    } catch (err) {
      alert(err.message || 'Failed to delete');
    }
  };

  const principalOf = (article) => article.totalPrincipal ?? article.amount ?? 0;

  return (
    <Stack spacing={{ base: 4, md: 6 }}>
      <Box>
        <Heading size={{ base: 'md', md: 'lg' }}>Released</Heading>
        <Text color="gray.500" fontSize="sm">
          All released items with interest earned at release (first 30 days = 1 month, then daily; 31-day months count as 30).
        </Text>
      </Box>

      <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={{ base: 3, md: 4 }}>
        <form onSubmit={applyFilters}>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} spacing={4}>
            <Box>
              <Text fontSize="sm" mb={1}>Search Name/Client</Text>
              <Input name="search" value={filters.search} onChange={handleFilterChange} placeholder="Client or item no" />
            </Box>
            <Box>
              <Text fontSize="sm" mb={1}>Search Amount</Text>
              <Input type="number" name="amountSearch" value={filters.amountSearch} onChange={handleFilterChange} placeholder="Principal" />
            </Box>
            <Box>
              <Text fontSize="sm" mb={1}>Release from</Text>
              <Input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} />
            </Box>
            <Box>
              <Text fontSize="sm" mb={1}>Release to</Text>
              <Input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} />
            </Box>
            <Box alignSelf={{ base: 'stretch', md: 'flex-end' }}>
              <Stack direction={{ base: 'column', sm: 'row' }} spacing={2}>
                <Button type="submit" colorScheme="blue" w={{ base: 'full', sm: 'auto' }}>Filter</Button>
                <Button variant="outline" onClick={clearFilters} w={{ base: 'full', sm: 'auto' }}>Clear</Button>
              </Stack>
            </Box>
          </SimpleGrid>
        </form>
      </Box>

      {error && <Text color="red.500">{error}</Text>}

      <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={{ base: 3, md: 4 }}>
        <Box overflowX="auto">
          <table style={{ width: '100%', minWidth: '960px', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Client</th>
                <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Item</th>
                <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Loan Date</th>
                <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Release Date</th>
                <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Principal</th>
                <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Interest Earned</th>
                <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Received</th>
                <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Rate</th>
                <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {articles.length ? articles.map((article) => {
                const interest = releaseInterestOf(article);
                return (
                  <tr key={article._id}>
                    <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{article.client?.name || 'N/A'}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{article.nameOrNo || 'N/A'}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{formatDate(article.date)}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{formatDate(article.releaseDate)}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right', fontWeight: 'bold' }}>
                      {fmtMoney(principalOf(article))}
                    </td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right' }}>
                      {interest != null ? fmtMoney(interest) : 'N/A'}
                    </td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right' }}>
                      {article.releaseAmount != null ? fmtMoney(article.releaseAmount) : 'N/A'}
                    </td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right' }}>
                      {getArticleInterestRate(article) ? `${getArticleInterestRate(article)}%` : '-'}
                    </td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Button size="xs" colorScheme="blue" variant="ghost" mr={2} onClick={() => setViewArticle(article)}>
                        View
                      </Button>
                      {canEdit && (
                        <Button size="xs" colorScheme="gray" variant="outline" mr={2} onClick={() => openEdit(article)}>
                          Edit
                        </Button>
                      )}
                      {canDelete && (
                        <Button size="xs" variant="outline" colorScheme="red" onClick={() => handleDelete(article)}>
                          Delete
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td style={{ padding: '8px' }} colSpan={9}>No released items found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </Box>
      </Box>

      <Dialog.Root
        open={!!viewArticle}
        onOpenChange={(e) => {
          if (!e.open) setViewArticle(null);
        }}
        size="xl"
      >
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content maxH="85vh" overflowY="auto">
            <Dialog.Header>
              <Dialog.Title>Released: {viewArticle?.nameOrNo}</Dialog.Title>
              <Dialog.CloseTrigger onClick={() => setViewArticle(null)} />
            </Dialog.Header>
            <Dialog.Body>
              {viewArticle && (
                <Stack spacing={4}>
                  <Badge colorPalette="green" w="fit-content">Released</Badge>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <Box><Text fontWeight="bold">Client</Text><Text>{viewArticle.client?.name || 'N/A'}</Text></Box>
                    <Box><Text fontWeight="bold">Item</Text><Text>{viewArticle.nameOrNo || 'N/A'}</Text></Box>
                    <Box><Text fontWeight="bold">Loan Date</Text><Text>{formatDate(viewArticle.date)}</Text></Box>
                    <Box><Text fontWeight="bold">Release Date</Text><Text>{formatDate(viewArticle.releaseDate)}</Text></Box>
                    <Box><Text fontWeight="bold">Weight</Text><Text>{viewArticle.weight ? `${viewArticle.weight}g` : 'N/A'}</Text></Box>
                    <Box><Text fontWeight="bold">Interest Rate</Text><Text>{getArticleInterestRate(viewArticle) || '-'}%</Text></Box>
                    <Box><Text fontWeight="bold">Principal</Text><Text>{fmtMoney(principalOf(viewArticle))}</Text></Box>
                    <Box>
                      <Text fontWeight="bold">Interest Earned</Text>
                      <Text>{fmtMoney(releaseInterestOf(viewArticle))}</Text>
                    </Box>
                    <Box><Text fontWeight="bold">Received Amount</Text><Text>{fmtMoney(viewArticle.releaseAmount)}</Text></Box>
                    <Box><Text fontWeight="bold">ID Proof</Text><Text>{formatIdProof(viewArticle.identityProof)}</Text></Box>
                    <Box><Text fontWeight="bold">Address</Text><Text>{viewArticle.address || 'N/A'}</Text></Box>
                    <Box><Text fontWeight="bold">Description</Text><Text>{viewArticle.description || 'N/A'}</Text></Box>
                  </SimpleGrid>

                  <Text fontWeight="bold" fontSize="lg" mt={2}>Loan History</Text>
                  <Box overflowX="auto">
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Date</th>
                          <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Type</th>
                          <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Amount</th>
                          <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(viewArticle.loans?.length
                          ? viewArticle.loans
                          : [{ date: viewArticle.date, description: 'Initial', amount: viewArticle.amount, interestRate: viewArticle.interestRate }]
                        ).map((loan, idx) => (
                          <tr key={loan._id || idx}>
                            <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{formatDate(loan.date)}</td>
                            <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{loan.description || (idx === 0 ? 'Initial' : 'Top-up')}</td>
                            <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right' }}>{fmtMoney(loan.amount)}</td>
                            <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right' }}>{loan.interestRate || viewArticle.interestRate}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>

                  {viewArticle.payments?.length > 0 && (
                    <>
                      <Text fontWeight="bold" fontSize="lg">Payments</Text>
                      <Box overflowX="auto">
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                          <thead>
                            <tr>
                              <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Date</th>
                              <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Type</th>
                              <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {viewArticle.payments.map((p, idx) => (
                              <tr key={p._id || idx}>
                                <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{formatDate(p.date)}</td>
                                <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>
                                  {p.type}{p.type === 'interest' && p.months ? ` (${p.months} mo)` : ''}
                                </td>
                                <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right' }}>{fmtMoney(p.amount)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </Box>
                    </>
                  )}
                </Stack>
              )}
            </Dialog.Body>
            <Dialog.Footer>
              {canEdit && viewArticle && (
                <Button colorScheme="gray" variant="outline" mr={2} onClick={() => openEdit(viewArticle)}>Edit</Button>
              )}
              {canDelete && viewArticle && (
                <Button colorScheme="red" variant="outline" mr={2} onClick={() => handleDelete(viewArticle)}>Delete</Button>
              )}
              <Button variant="ghost" onClick={() => setViewArticle(null)}>Close</Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>

      <Dialog.Root
        open={!!editingArticle}
        onOpenChange={(e) => {
          if (!e.open) setEditingArticle(null);
        }}
        size="lg"
      >
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>Edit Released Item</Dialog.Title>
              <Dialog.CloseTrigger onClick={() => setEditingArticle(null)} />
            </Dialog.Header>
            <Dialog.Body>
              <Stack spacing={4}>
                <Box>
                  <Text fontSize="sm" mb={1}>Name/No</Text>
                  <Input name="nameOrNo" value={editForm.nameOrNo || ''} onChange={handleEditChange} />
                </Box>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <Box>
                    <Text fontSize="sm" mb={1}>Release Date</Text>
                    <Input type="date" name="releaseDate" value={editForm.releaseDate || ''} onChange={handleEditChange} />
                  </Box>
                  <Box>
                    <Text fontSize="sm" mb={1}>Received Amount</Text>
                    <Input type="number" name="releaseAmount" value={editForm.releaseAmount} onChange={handleEditChange} />
                  </Box>
                  <Box>
                    <Text fontSize="sm" mb={1}>Weight</Text>
                    <Input type="number" name="weight" value={editForm.weight} onChange={handleEditChange} />
                  </Box>
                  <Box>
                    <Text fontSize="sm" mb={1}>Interest Rate</Text>
                    <Input type="number" name="interestRate" value={editForm.interestRate} onChange={handleEditChange} />
                  </Box>
                </SimpleGrid>
                <Text fontSize="sm" color="gray.600">
                  Interest earned for this release date: <b>{fmtMoney(previewInterest)}</b>
                </Text>
                <Box>
                  <Text fontSize="sm" mb={1}>Address</Text>
                  <Input name="address" value={editForm.address || ''} onChange={handleEditChange} />
                </Box>
                <Box>
                  <Text fontSize="sm" mb={1}>Description</Text>
                  <Input name="description" value={editForm.description || ''} onChange={handleEditChange} />
                </Box>
              </Stack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="ghost" mr={3} onClick={() => setEditingArticle(null)}>Cancel</Button>
              <Button colorScheme="blue" onClick={handleEditSubmit} isLoading={savingEdit}>Save Changes</Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
    </Stack>
  );
};

export default Released;
